import { IBusinessPlanChaptersResponse } from 'services/businessToolForm/types';

import {
  IBusinessPlanEntity,
  IBusinessPlanSectionEntity,
  IBusinessPlansLimits,
  IBusinessPlanStatus,
  IBusinessPlanType,
  IOpenLinkStatus,
} from 'interfaces/businessPlan';
import { ICreatedUpdatedDates, IIdentifiable } from 'interfaces/common';

import {
  ACCOMMODATION_SERVICES_SECTOR,
  ADMINISTRATIVE_AND_SUPPORT_SERVICES_SECTOR,
  ARTS_ENTERTAINMENT_SECTOR,
  CONSTRUCTION_SECTOR,
  EDUCATIONAL_SERVICES_SECTOR,
  FINANCE_AND_INSURANCE_SECTOR,
  FISHING_AND_HUNTING_SECTOR,
  HEALTH_CARE_SECTOR,
  INFORMATION_SECTOR,
  MANAGEMENT_OF_COMPANIES_SECTOR,
  MANUFACTURING_SECTOR,
  MINING_QUARRYING_OIL_AND_GAS_SECTOR,
  OTHER_SERVICES_SECTOR,
  PUBLIC_ADMINISTRATION_SECTOR,
  REAL_ESTATE_SECTOR,
  RETAIL_TRADE_SECTOR,
  SCIENTIFIC_SERVICES_SECTOR,
  TRANSPORTATION_AND_WAREHOUSING_SECTOR,
  UTILITIES_SECTOR,
  WHOLESALE_TRADE_SECTOR,
} from 'components/BusinessToolForm/constants/industrySectorOptions';

import { ObjectValues, OmitKeysInAnother } from 'helpers/types';

interface IIdEntity {
  id: string;
}

export interface IGetAllBusinessPlansParams {
  page: number;
  size: number;
  sort: string[];
}

export interface IAllBusinessPlansResponse {
  total: number;
  size: number;
  pages: number;
  content: IBusinessPlanEntity[];
}

export interface IBusinessPlanResponse extends Omit<IBusinessPlanEntity, 'metadata'> {
  metadata: string;
}

export interface ICreateBusinessPlanBody
  extends Omit<
    Partial<Omit<OmitKeysInAnother<IBusinessPlanEntity, ICreatedUpdatedDates>, 'uuid' | 'viewonly'>>,
    'title' | 'type'
  > {
  title: string;
  type: IBusinessPlanType;
  sections?: IBusinessPlanSectionEntity[];
}
export interface ICreateBusinessPlanFromOnePage {
  businessPlanUuid: string;
  businessPlanType: IBusinessPlanType;
}

export interface ICreateBusinessPlanBodyResponse extends IBusinessPlanEntity {}

// UPDATE

export interface IUpdateBusinessPlanParams {
  id: string;
  body: Partial<
    Omit<OmitKeysInAnother<IBusinessPlanEntity, ICreatedUpdatedDates>, 'uuid' | 'viewonly' | 'metadata'>
  > & {
    metadata?: string;
  };
}

export interface IUpdateBusinessPlanBodyResponse extends IBusinessPlanEntity {}

// LIMITS

export interface IGetLimitsResponse extends IBusinessPlansLimits {}

export interface IDuplicateBusinessPlanResponse extends IBusinessPlanEntity {}

// EDIT ACCESS

export const PLAN_ACCESS_TYPE = {
  VIEW: 'VIEW',
  EDIT: 'EDIT',
} as const;

export type IPlanAccessType = ObjectValues<typeof PLAN_ACCESS_TYPE>;

export interface IGrantEditAccessParams extends IIdentifiable {
  body: {
    email: string;
    type: IPlanAccessType;
  };
}

export interface IGrantEditAccessResponse {}

export interface IUsersWithAccessItem {
  email: string;
  businessPlanUuid: string;
  type: IPlanAccessType;
}

export interface IGetUsersWithAccessParams extends IIdentifiable {
  type?: IPlanAccessType;
}

export interface IGetUsersWithAccessResponse {
  total: number;
  pages: number;
  size: number;
  content: IUsersWithAccessItem[];
}

export interface IDeleteUserAccessParams extends IIdentifiable {
  email: string;
}

export const INDUSTRY_TYPE: Record<string, string> = {
  [FISHING_AND_HUNTING_SECTOR]: 'fishingAndHunting',
  [MINING_QUARRYING_OIL_AND_GAS_SECTOR]: 'miningQuarryingOilAndGas',
  [UTILITIES_SECTOR]: 'utilities',
  [CONSTRUCTION_SECTOR]: 'construction',
  [MANUFACTURING_SECTOR]: 'manufacturing',
  [WHOLESALE_TRADE_SECTOR]: 'wholesaleTrade',
  [RETAIL_TRADE_SECTOR]: 'retailTrade',
  [TRANSPORTATION_AND_WAREHOUSING_SECTOR]: 'transportationAndWarehousing',
  [INFORMATION_SECTOR]: 'information',
  [FINANCE_AND_INSURANCE_SECTOR]: 'financeAndInsurance',
  [REAL_ESTATE_SECTOR]: 'realEstate',
  [SCIENTIFIC_SERVICES_SECTOR]: 'scientificServices',
  [MANAGEMENT_OF_COMPANIES_SECTOR]: 'managementOfCompanies',
  [ADMINISTRATIVE_AND_SUPPORT_SERVICES_SECTOR]: 'administrativeAndSupportServices',
  [EDUCATIONAL_SERVICES_SECTOR]: 'educationalServices',
  [HEALTH_CARE_SECTOR]: 'healthCare',
  [ARTS_ENTERTAINMENT_SECTOR]: 'artsEntertainment',
  [ACCOMMODATION_SERVICES_SECTOR]: 'accommodationServices',
  [OTHER_SERVICES_SECTOR]: 'otherServices',
  [PUBLIC_ADMINISTRATION_SECTOR]: 'publicAdministration',
} as const;

export type IIndustryType = ObjectValues<typeof INDUSTRY_TYPE>;
export interface IGetIndustryPictutesParams {
  type: IIndustryType;
  enabled?: boolean;
}

export type SectionNumber = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12';
export type SectionFileName = 'Backside' | 'Contents' | 'Title' | `Section${SectionNumber}`;
export type SectionFileNameResponse = `${SectionFileName}.jpg`;

export interface IIndustruPictureItem {
  expiresAt: string;
  filename: SectionFileNameResponse;
  url: string;
}

export interface IShareOnePageByMailBody extends IIdEntity {
  email: string;
}

export interface IGetPublicStatusParams extends IIdEntity {}

export interface IGetPublicStatusResponse {
  title: string;
  type: IBusinessPlanType;
  status: IBusinessPlanStatus;
  openLinkStatus: IOpenLinkStatus;
}

export interface IPatchPublicStatusParams extends IIdEntity {
  action: 'open' | 'close';
}

export interface IGetPublicOnePagePlanParams extends IIdEntity {}

export interface IGetPublicOnePagePlanResponse {
  plan: IBusinessPlanResponse;
  chapters: IBusinessPlanChaptersResponse;
}
